import { rpgAtom, sessionsLimitReachedAtom } from "atoms/app.atom"
import { activeConversationSelector, conversationTaskUpdatedAtom, IMessage } from "atoms/conversation.atom"
import { ReceivedWebhookMonitor, receivedWebhookMonitorAtom } from "atoms/workflow.atom"
import { EventUserWriting, EventWorkspace, workspaceStateAtom, writingAtom } from "atoms/workspace.atom"
import { useStore, useStoreActions, useStoreState } from "hooks"
import { decodeJwt, getHost } from "lib/helper"
import toast from "lib/toast"
import { throttle } from "lodash"
import React, { useCallback, useEffect, useRef } from "react"
import { useAtom, useSetAtom } from "jotai"
import { Socket, io } from "socket.io-client"
import { session } from "store"
import { GameState, Round, RpgEventType } from "pages/agents/components/RpgChat/rpg.types"

interface useSocketV2Props {
  socketUrl: string
  offlineByInactivity: boolean
}

let workspaceStateRaw: EventWorkspace = {
  users: {},
}
export let v2Socket: Socket | null = null
function useSocketV2(props: useSocketV2Props) {
  const { workspace, user, logout } = useStore(session)
  const setWorkspaceState = useSetAtom(workspaceStateAtom)
  const setSessionsLimitReached = useSetAtom(sessionsLimitReachedAtom)
  const setWriting = useSetAtom(writingAtom)
  const setReceivedWebhookMonitor = useSetAtom(receivedWebhookMonitorAtom)
  const integrationsStore = useStoreState((state) => state.integrations)
  const integrationsActions = useStoreActions((actions) => actions.integrations)
  const [activeConversation, setActiveConversation] = useAtom(activeConversationSelector)
  const setConversationTaskUpdated = useSetAtom(conversationTaskUpdatedAtom)
  const [gameState, setGameState] = useAtom(rpgAtom)

  //   const socketRef = useRef<Socket | null>(null)
  const dependenciesRef = useRef<any>(null)

  const saveWorkspaceState = useCallback(() => {
    // Iterar sobre os membros na estrutura original
    // Iterar sobre as chaves do objeto `users` na estrutura original
    let newWorkspaceState = {
      conversations: {},
      users: workspaceStateRaw?.users,
    }
    for (const userIdKey in workspaceStateRaw?.users) {
      // Obter o membro correspondente ao userIdKey
      const user = workspaceStateRaw?.users[userIdKey]
      const userId = user.userId

      // Iterar sobre as chaves do objeto `viewingConversationIds` do membro
      for (const conversationKey in user.viewingConversationIds) {
        const conversationId = user.viewingConversationIds[conversationKey]

        // Se a conversa não estiver presente, inicializá-la
        if (!newWorkspaceState?.conversations?.[conversationId]) {
          newWorkspaceState.conversations[conversationId] = {
            viewingUserIds: {},
          }
        }

        // Marcar que o membro está visualizando a conversa
        newWorkspaceState.conversations[conversationId].viewingUserIds[userId] = true
      }
    }

    setWorkspaceState(newWorkspaceState)
  }, [setWorkspaceState])

  const throttledSaveWorkspaceState = useCallback(throttle(saveWorkspaceState, 1000), [saveWorkspaceState])

  useEffect(() => {
    dependenciesRef.current = {
      throttledSaveWorkspaceState,
      integrationsStore,
      integrationsActions,
      setReceivedWebhookMonitor,
      activeConversation,
      setConversationTaskUpdated,
      setActiveConversation,
    }
  }, [
    activeConversation,
    integrationsActions,
    integrationsStore,
    setConversationTaskUpdated,
    setReceivedWebhookMonitor,
    throttledSaveWorkspaceState,
    setActiveConversation,
  ])

  useEffect(() => {
    if (props.offlineByInactivity === true) {
      console.log("Desconectando socket por inatividade")
      v2Socket?.disconnect?.()
    } else if (!v2Socket?.connected && props.offlineByInactivity === false) {
      console.log("Reconectando socket por inatividade")
      v2Socket?.connect?.()
    }
  }, [props.offlineByInactivity])

  useEffect(() => {
    if (!!v2Socket || !props.socketUrl) {
      return
    }

    // Inicializar o socket
    v2Socket = io(props.socketUrl, {
      query: {
        token: workspace?.token,
        isInspecting:
          getHost()?.subdomain == "view" || getHost()?.subdomain == "partner-inspect" || getHost()?.subdomain == "partner-inspect2",
      },
      transports: ["websocket"], // Utiliza WebSockets como transporte primário
      reconnectionAttempts: 5, // Tentar reconectar 5 vezes
      reconnectionDelay: 5000, // Intervalo de 5000 ms entre tentativas de reconexão
    })

    // setSocket(socketRef.current)

    v2Socket?.on("connect", () => {
      !import.meta.env?.PROD && console.log("🧲 V2 connected")
    })

    v2Socket?.on("workspace:joined", (dto: { workspace: EventWorkspace }) => {
      // !import.meta.env?.PROD && console.log("🧲 V2 workspace:joined", dto)
      //   dependenciesRef.current?.setWorkspaceState(dto.workspace)
      workspaceStateRaw = dto.workspace
      dependenciesRef.current?.throttledSaveWorkspaceState()
    })

    v2Socket?.on(
      "session:limit-reached",
      (dto: {
        sessions: {
          [token: string]: {
            sessionCreatedAt: string
            device: string
            ip: string
            isMobile: boolean
            sessionId: string
          }
        }
      }) => {
        setSessionsLimitReached(dto)
      },
    )

    v2Socket?.on("session:disconnect-from-other-session", (dto: { userId: string; sessionId: string }) => {
      !import.meta.env?.PROD && console.log("🧲 logout", dto)
      if (dto.userId == user?.id) {
        const decoded = decodeJwt(workspace.token)
        if (dto.sessionId === decoded.payload.sessionId) {
          toast.info("Você foi deslogado por outra sessão...")
          setTimeout(() => {
            logout({ notNotifyV0: true })
          }, 3000)
        }
      }
    })
    v2Socket?.on("integration:updated", (dto: { type: "zoho"; data: any }) => {
      console.log("🧲 V2 integration:updated", dto)
      const item = dependenciesRef.current?.integrationsStore.all.find((x) => x.type == dto.type)
      if (item && dto.data.tokenResult) {
        dependenciesRef.current?.integrationsActions.save({
          ...item,
          data: {
            ...item.data,
            tokenResult: { ...item.data, ...dto.data.tokenResult },
          },
        })
      }
    })

    v2Socket?.on("user:updated", (dto: { user: EventWorkspace["users"][0] }) => {
      // !import.meta.env?.PROD && console.log("🧲 V2 user:updated", dto)

      if (dto?.user) {
        workspaceStateRaw = {
          ...workspaceStateRaw,
          users: {
            ...workspaceStateRaw?.users,
            [dto.user.userId]: dto.user,
          },
        }
        dependenciesRef.current?.throttledSaveWorkspaceState()
      }
    })

    v2Socket?.on("inbox:message-system:created", (dto: { message: IMessage }) => {
      console.log("🧲 V2 inbox:message-system:created", dto)
      const isActiveConversation = dependenciesRef.current?.activeConversation?.id == dto?.message?.customerPlatformId

      if (isActiveConversation) {
        dependenciesRef.current?.setActiveConversation((x) => {
          let messages = [...(x?.messages || [])]

          messages = [dto?.message, ...messages]

          return {
            ...x!,
            messages,
          }
        })
      }
    })

    v2Socket?.on("inbox:message-system:updated", (dto: { message: IMessage }) => {
      console.log("🧲 V2 inbox:message-system:updated", dto)
      const isActiveConversation = dependenciesRef.current?.activeConversation?.id == dto?.message?.customerPlatformId
      if (isActiveConversation) {
        dependenciesRef.current?.setActiveConversation((x) => {
          let messages = [...(x?.messages || [])]

          messages = messages.map((m) => (m.id == dto?.message?.id ? dto?.message : m))

          return {
            ...x!,
            messages,
          }
        })
      }
    })

    v2Socket?.on("user:writing", (dto: EventUserWriting) => {
      setWriting(dto)
    })

    v2Socket?.on(
      "rpg:event",
      (dto: {
        type: RpgEventType
        data: {
          gameState?: GameState
          round?: Round
        }
      }) => {
        console.log("🧲 V2 rpg:event", dto)
        if (dto.type === RpgEventType.GameStateUpdated && dto.data.gameState) {
          // setGameState(dto.data.gameState)
          setGameState((gs) => ({
            ...gs!,
            ...dto.data.gameState,
          }))
        }
        if (dto.type === RpgEventType.RoundUpdated && dto.data.round) {
          setGameState((gs) => ({
            ...gs!,
            rounds: gs?.rounds?.map((round) => (round.id === dto.data.round?.id ? dto.data.round : round)) || [],
          }))
        }
        if (dto.type === RpgEventType.GeneratingRound) {
          setGameState((gs) => ({
            ...gs!,
            generatingRound: true,
          }))
        }
      },
    )

    // cadence updated
    v2Socket?.on(
      "task:updated",
      (dto: {
        tasks?: {
          taskId?: string
          dealId?: string
          conversationId?: string
        }[]
      }) => {
        !import.meta.env?.PROD && console.log("🔥 task:updated", dto)
        for (let index = 0; index < (dto.tasks || [])?.length; index++) {
          const task = dto.tasks?.[index]
          if (dependenciesRef.current?.activeConversation?.id == task?.conversationId) {
            dependenciesRef.current?.setConversationTaskUpdated((c) => c + 1)
          }
        }
        // Se devo atualizar conversa ative
      },
    )

    v2Socket?.on("webhook:inbound:monitor", (dto: { socketId: string; received: ReceivedWebhookMonitor }) => {
      console.log("🧲 V2 webhook:inbound:monitor", dto)
      dependenciesRef.current?.setReceivedWebhookMonitor(dto.received)
    })
  }, [props.socketUrl, workspace?.token, setWriting])
}

export default useSocketV2
